import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useActivityTypes, type ActivityType } from "@/hooks/useActivityTypes";
import { useOrganization } from "@/hooks/useOrganization";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NumberInput } from "@/components/ui/number-input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";

const TOURNAMENT_FORMATS = [
  { value: "", labelKey: "none" },
  { value: "americano", labelKey: "americano" },
  { value: "mexicano", labelKey: "mexicano" },
  { value: "team_americano", labelKey: "teamAmericano" },
  { value: "team_mexicano", labelKey: "teamMexicano" },
  { value: "knockout", labelKey: "knockout" },
];

const GENDER_FILTER_OPTIONS = [
  { value: "all", labelKey: "all" },
  { value: "herr", labelKey: "men" },
  { value: "dam", labelKey: "women" },
  { value: "mix", labelKey: "mix" },
];

const APPROVAL_MODE_OPTIONS = [
  { value: "auto", labelKey: "automatic" },
  { value: "manual", labelKey: "manual" },
];

const PAYMENT_MODE_OPTIONS = [
  { value: "pay_on_join", labelKey: "payOnJoin" },
  { value: "pay_later", labelKey: "payLater" },
  { value: "free", labelKey: "free" },
];

function emptyDraft(organizationId: string): Partial<ActivityType> & { organization_id: string } {
  return {
    organization_id: organizationId,
    name: "",
    name_en: null,
    slug: "",
    icon: "\u{1F3BE}",
    color: "#6366f1",
    description: "",
    description_en: null,
    player_can_create: false,
    result_reporting: false,
    default_spots: 4,
    default_max_participants: null,
    min_participants: null,
    default_duration_minutes: 60,
    default_min_level: null,
    default_max_level: null,
    default_gender_filter: null,
    default_approval_mode: null,
    default_payment_mode: null,
    requires_courts: true,
    min_courts: null,
    max_courts: null,
    is_system_default: false,
    tournament_format: null,
    default_points_per_match: 32,
    title_placeholder: "",
    is_active: true,
    sort_order: 100,
  };
}

export default function SettingsActivityTypes() {
  const { t } = useTranslation("admin");
  const { organization } = useOrganization();
  const { allTypes, isLoading, upsert, remove } = useActivityTypes();

  const [editOpen, setEditOpen] = useState(false);
  const [draft, setDraft] = useState<Partial<ActivityType> & { organization_id: string } | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  const openCreate = () => {
    if (!organization?.id) return;
    setDraft(emptyDraft(organization.id));
    setEditOpen(true);
  };

  const openEdit = (t: ActivityType) => {
    setDraft({ ...t });
    setEditOpen(true);
  };

  const handleSave = () => {
    if (!draft) return;
    upsert.mutate(draft as Partial<ActivityType> & { organization_id: string }, {
      onSuccess: () => setEditOpen(false),
    });
  };

  const handleDelete = (id: string) => {
    remove.mutate(id, {
      onSuccess: () => setDeleteConfirmId(null),
    });
  };

  const handleToggleActive = (t: ActivityType) => {
    upsert.mutate({
      id: t.id,
      organization_id: t.organization_id,
      is_active: !t.is_active,
    });
  };

  const setField = <K extends keyof ActivityType>(key: K, value: ActivityType[K]) => {
    setDraft((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const isSystemDefault = draft?.is_system_default ?? false;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-1">{t("settings.activityTypes.title")}</h3>
        <p className="text-sm text-muted-foreground mb-4">
          {t("settings.activityTypes.description")}
        </p>
      </div>

      <div className="grid gap-3">
        {allTypes.map((at) => (
          <Card
            key={at.id}
            className={`transition-colors ${!at.is_active ? "opacity-60" : ""}`}
          >
            <CardContent className="flex items-center gap-4 p-4">
              <span className="text-2xl shrink-0">{at.icon}</span>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-sm">{at.name}</span>
                  {at.is_system_default && (
                    <Badge variant="outline" className="text-[10px]">System</Badge>
                  )}
                  {at.player_can_create && (
                    <Badge variant="secondary" className="text-[10px]">{t("settings.activityTypes.badgePlayerCreated")}</Badge>
                  )}
                  {at.result_reporting && (
                    <Badge variant="secondary" className="text-[10px]">{t("settings.activityTypes.badgeResult")}</Badge>
                  )}
                  {at.tournament_format && (
                    <Badge variant="secondary" className="text-[10px]">
                      {TOURNAMENT_FORMATS.find((f) => f.value === at.tournament_format)?.labelKey ? t(`settings.activityTypes.format_${TOURNAMENT_FORMATS.find((f) => f.value === at.tournament_format)!.labelKey}`) : at.tournament_format}
                    </Badge>
                  )}
                  {!at.requires_courts && (
                    <Badge variant="secondary" className="text-[10px]">{t("settings.activityTypes.badgeNoCourt")}</Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {at.default_spots} {t("settings.activityTypes.spots")} &middot; {at.default_duration_minutes} min
                </p>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <Switch
                  checked={at.is_active}
                  onCheckedChange={() => handleToggleActive(at)}
                  disabled={upsert.isPending}
                />
                <Button variant="ghost" size="icon" onClick={() => openEdit(at)}>
                  <Pencil className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Button variant="outline" className="gap-1" onClick={openCreate}>
        <Plus className="h-4 w-4" /> {t("settings.activityTypes.btnAdd")}
      </Button>

      {/* Edit / Create dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{draft?.id ? t("settings.activityTypes.dialogEditTitle") : t("settings.activityTypes.dialogCreateTitle")}</DialogTitle>
          </DialogHeader>

          {draft && (
            <div className="space-y-4 py-2">
              {/* ---- Section 1: Basic ---- */}
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t("settings.activityTypes.sectionBasicInfo")}</p>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t("settings.activityTypes.fieldName")}</Label>
                  <Input
                    value={draft.name ?? ""}
                    onChange={(e) => setField("name", e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t("settings.activityTypes.fieldNameEn")}</Label>
                  <Input
                    value={draft.name_en ?? ""}
                    onChange={(e) => setField("name_en", e.target.value || null)}
                    placeholder="English name"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t("settings.activityTypes.fieldDescription")}</Label>
                  <Input
                    value={draft.description ?? ""}
                    onChange={(e) => setField("description", e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t("settings.activityTypes.fieldDescriptionEn")}</Label>
                  <Input
                    value={draft.description_en ?? ""}
                    onChange={(e) => setField("description_en", e.target.value || null)}
                    placeholder="English description"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t("settings.activityTypes.fieldIcon")}</Label>
                  <Input
                    value={draft.icon ?? ""}
                    onChange={(e) => setField("icon", e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t("settings.activityTypes.fieldColor")}</Label>
                  <Input
                    value={draft.color ?? ""}
                    onChange={(e) => setField("color", e.target.value)}
                    placeholder="#6366f1"
                  />
                </div>
              </div>

              <Separator />

              {/* ---- Section 2: Defaults ---- */}
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t("settings.activityTypes.sectionDefaults")}</p>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>{t("settings.activityTypes.fieldDefaultSpots")}</Label>
                  <NumberInput
                    min={1}
                    value={draft.default_spots ?? 4}
                    onValueChange={(v) => setField("default_spots", v || 4)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t("settings.activityTypes.fieldMaxParticipants")}</Label>
                  <NumberInput
                    min={1}
                    value={draft.default_max_participants ?? undefined}
                    onValueChange={(v) => setField("default_max_participants", v || null)}
                    placeholder={t("settings.activityTypes.optional")}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t("settings.activityTypes.fieldMinParticipants")}</Label>
                  <NumberInput
                    min={1}
                    value={draft.min_participants ?? undefined}
                    onValueChange={(v) => setField("min_participants", v || null)}
                    placeholder={t("settings.activityTypes.optional")}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t("settings.activityTypes.fieldMinLevel")}</Label>
                  <NumberInput
                    min={0}
                    value={draft.default_min_level ?? undefined}
                    onValueChange={(v) => setField("default_min_level", v || null)}
                    placeholder={t("settings.activityTypes.optional")}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t("settings.activityTypes.fieldMaxLevel")}</Label>
                  <NumberInput
                    min={0}
                    value={draft.default_max_level ?? undefined}
                    onValueChange={(v) => setField("default_max_level", v || null)}
                    placeholder={t("settings.activityTypes.optional")}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>{t("settings.activityTypes.fieldDefaultDuration")}</Label>
                <NumberInput
                  min={15}
                  step={15}
                  value={draft.default_duration_minutes ?? 60}
                  onValueChange={(v) => setField("default_duration_minutes", v || 60)}
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>{t("settings.activityTypes.fieldGenderFilter")}</Label>
                  <Select
                    value={draft.default_gender_filter ?? "all"}
                    onValueChange={(v) => setField("default_gender_filter", v === "all" ? null : v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {GENDER_FILTER_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {t(`settings.activityTypes.gender_${o.labelKey}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t("settings.activityTypes.fieldApprovalMode")}</Label>
                  <Select
                    value={draft.default_approval_mode ?? "auto"}
                    onValueChange={(v) => setField("default_approval_mode", v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {APPROVAL_MODE_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {t(`settings.activityTypes.approval_${o.labelKey}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t("settings.activityTypes.fieldPaymentMode")}</Label>
                  <Select
                    value={draft.default_payment_mode ?? "pay_on_join"}
                    onValueChange={(v) => setField("default_payment_mode", v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAYMENT_MODE_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {t(`settings.activityTypes.payment_${o.labelKey}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center justify-between rounded-lg border p-3">
                <div>
                  <Label>{t("settings.activityTypes.playerCanCreate")}</Label>
                  <p className="text-xs text-muted-foreground">{t("settings.activityTypes.playerCanCreateDesc")}</p>
                </div>
                <Switch
                  checked={draft.player_can_create ?? false}
                  onCheckedChange={(v) => setField("player_can_create", v)}
                />
              </div>

              <Separator />

              {/* ---- Section 3: Courts ---- */}
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t("settings.activityTypes.sectionCourts")}</p>

              <div className="flex items-center justify-between rounded-lg border p-3">
                <div>
                  <Label>{t("settings.activityTypes.requiresCourts")}</Label>
                  <p className="text-xs text-muted-foreground">{t("settings.activityTypes.requiresCourtsDesc")}</p>
                </div>
                <Switch
                  checked={draft.requires_courts ?? true}
                  onCheckedChange={(v) => setField("requires_courts", v)}
                />
              </div>

              {(draft.requires_courts ?? true) && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t("settings.activityTypes.fieldMinCourts")}</Label>
                    <NumberInput
                      min={1}
                      value={draft.min_courts ?? undefined}
                      onValueChange={(v) => setField("min_courts", v || null)}
                      placeholder={t("settings.activityTypes.optional")}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t("settings.activityTypes.fieldMaxCourts")}</Label>
                    <NumberInput
                      min={1}
                      value={draft.max_courts ?? undefined}
                      onValueChange={(v) => setField("max_courts", v || null)}
                      placeholder={t("settings.activityTypes.optional")}
                    />
                  </div>
                </div>
              )}

              <Separator />

              {/* ---- Section 4: Visibility ---- */}
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t("settings.activityTypes.sectionVisibility")}</p>

              <div className="flex items-center justify-between rounded-lg border p-3">
                <div>
                  <Label>{t("settings.activityTypes.fieldActive")}</Label>
                  <p className="text-xs text-muted-foreground">{t("settings.activityTypes.fieldActiveDesc")}</p>
                </div>
                <Switch
                  checked={draft.is_active ?? true}
                  onCheckedChange={(v) => setField("is_active", v)}
                />
              </div>

              <div className="space-y-2">
                <Label>{t("settings.activityTypes.fieldSortOrder")}</Label>
                <NumberInput
                  min={0}
                  value={draft.sort_order ?? 100}
                  onValueChange={(v) => setField("sort_order", v || 0)}
                />
              </div>

              {/* ---- Tournament (keep existing) ---- */}
              <Separator />

              <div className="flex items-center justify-between rounded-lg border p-3">
                <div>
                  <Label>{t("settings.activityTypes.resultReporting")}</Label>
                  <p className="text-xs text-muted-foreground">{t("settings.activityTypes.resultReportingDesc")}</p>
                </div>
                <Switch
                  checked={draft.result_reporting ?? false}
                  onCheckedChange={(v) => setField("result_reporting", v)}
                />
              </div>

              <div className="space-y-2">
                <Label>{t("settings.activityTypes.fieldTournamentFormat")}</Label>
                <Select
                  value={draft.tournament_format ?? ""}
                  onValueChange={(v) => setField("tournament_format", v || null)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t("settings.activityTypes.format_none")} />
                  </SelectTrigger>
                  <SelectContent>
                    {TOURNAMENT_FORMATS.map((f) => (
                      <SelectItem key={f.value || "__none"} value={f.value || "__none"}>
                        {t(`settings.activityTypes.format_${f.labelKey}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {draft.tournament_format && (
                <div className="space-y-2">
                  <Label>{t("settings.activityTypes.fieldPointsPerMatch")}</Label>
                  <NumberInput
                    min={1}
                    value={draft.default_points_per_match ?? 32}
                    onValueChange={(v) => setField("default_points_per_match", v || 32)}
                  />
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            {draft?.id && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex">
                      <Button
                        variant="destructive"
                        size="sm"
                        disabled={isSystemDefault}
                        onClick={() => {
                          if (isSystemDefault) return;
                          setEditOpen(false);
                          setDeleteConfirmId(draft.id!);
                        }}
                      >
                        <Trash2 className="h-4 w-4 mr-1" /> {t("settings.activityTypes.btnDelete")}
                      </Button>
                    </span>
                  </TooltipTrigger>
                  {isSystemDefault && (
                    <TooltipContent>
                      <p>{t("settings.activityTypes.systemTypeCannotDelete")}</p>
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            )}
            <div className="flex-1" />
            <Button variant="outline" onClick={() => setEditOpen(false)}>
              {t("settings.activityTypes.btnCancel")}
            </Button>
            <Button onClick={handleSave} disabled={upsert.isPending || !draft?.name}>
              {upsert.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              {t("settings.activityTypes.btnSave")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteConfirmId} onOpenChange={() => setDeleteConfirmId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("settings.activityTypes.deleteTitle")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {t("settings.activityTypes.deleteConfirm")}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmId(null)}>
              {t("settings.activityTypes.btnCancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteConfirmId && handleDelete(deleteConfirmId)}
              disabled={remove.isPending}
            >
              {remove.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              {t("settings.activityTypes.btnDelete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Separator className="my-6" />

      {/* ---- "Vad ingar" options ---- */}
      <IncludesOptionsEditor organizationId={organization?.id} settings={organization?.settings} />
    </div>
  );
}

// ---- Editable "Vad ingar" options ----

interface IncludesOption {
  id: string;
  label: string;
  icon: string;
}

function IncludesOptionsEditor({
  organizationId,
  settings,
}: {
  organizationId: string | undefined;
  settings: Record<string, any> | undefined;
}) {
  const queryClient = useQueryClient();
  const existing: IncludesOption[] = (settings as any)?.activity_includes_options ?? [];
  const [items, setItems] = useState<IncludesOption[]>(existing);

  const addItem = () => {
    setItems((prev) => [
      ...prev,
      { id: `custom_${Date.now()}`, label: "", icon: "" },
    ]);
  };

  const removeItem = (idx: number) => {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateItem = (idx: number, field: "label" | "icon", value: string) => {
    setItems((prev) =>
      prev.map((item, i) =>
        i === idx ? { ...item, [field]: value } : item,
      ),
    );
  };

  const { t } = useTranslation("admin");
  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!organizationId) throw new Error("No org");
      const merged = {
        ...((settings as Record<string, unknown>) ?? {}),
        activity_includes_options: items.filter((i) => i.label.trim()),
      };
      const { error } = await supabase
        .from("organizations")
        .update({ settings: merged } as Record<string, unknown>)
        .eq("id", organizationId);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success(t("settings.activityTypes.optionsSaved"));
      queryClient.invalidateQueries({ queryKey: ["admin-org-settings"] });
    },
    onError: (err: Error) => toast.error(err.message || t("settings.activityTypes.errorCouldNotSave")),
  });

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold mb-1">{t("settings.activityTypes.includesTitle")}</h3>
        <p className="text-sm text-muted-foreground mb-4">
          {t("settings.activityTypes.includesDescription")}
        </p>
      </div>

      <div className="space-y-2">
        {items.map((item, idx) => (
          <div key={item.id} className="flex items-center gap-2">
            <Input
              className="w-16 h-8 text-center text-sm"
              placeholder={t("settings.activityTypes.placeholderEmoji")}
              value={item.icon}
              onChange={(e) => updateItem(idx, "icon", e.target.value)}
            />
            <Input
              className="flex-1 h-8 text-sm"
              placeholder={t("settings.activityTypes.placeholderLabel")}
              value={item.label}
              onChange={(e) => updateItem(idx, "label", e.target.value)}
            />
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0"
              onClick={() => removeItem(idx)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>

      <div className="flex gap-2">
        <Button variant="outline" size="sm" className="gap-1" onClick={addItem}>
          <Plus className="h-4 w-4" /> {t("settings.activityTypes.btnAddOption")}
        </Button>
        <Button
          size="sm"
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isPending}
        >
          {saveMutation.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
          {t("settings.activityTypes.btnSave")}
        </Button>
      </div>
    </div>
  );
}
